<script setup lang="ts">
import type BaseEmail from '@/components/user/account/Email.vue';
import type BasePhone from '@/components/user/account/Phone.vue';
import type BaseAddress from '@/components/user/account/Address.vue';
import type BaseDocument from '@/components/user/account/Document.vue';
import type BaseContracts from '@/components/user/account/Contracts.vue';
import type BaseBankMex from '@/components/user/account/BankAccounts/Mex.vue';
import type BasePix from '@/components/user/account/Pix.vue';
import type BaseBankAccounts from '@/components/user/account/BankAccounts.vue';
import type BaseInfos from '@/components/user/account/Infos.vue';
import { AccountProtectionAccordion } from '@/types/UserLimits';

const route = useRoute();
const { t } = useTexts();
const { setCurrentMenuOpenByGroup } = useAccountMenuStore();
const { getMinValueToTriggerModule } = useModulesValidation();

const {
  isContractsSectionEnabled,
  userMigrationNewDomain,
  isWithinMigrationPeriod,
  fetchEmailUpdateFeatureFlag,
} = useFeatureFlagsNew();

const isEmailUpdateEnabled = ref<boolean>(false);
const { isBankAccountEnabled, isUserV2Enabled } = useFeatureFlagsJson();

const { walletRealNumber } = useWallet();
const { checkPending } = useUserSettingsPending();

const {
  fetchUserProfile,
  userEmail,
  userCurrency,
  userCountry,
  userHasDocument,
} = useUserData();

useSeo({
  title: t('account.account_details'),
});

const emailRef = ref<InstanceType<typeof BaseEmail> | null>(null);
const phoneRef = ref<InstanceType<typeof BasePhone> | null>(null);
const addressRef = ref<InstanceType<typeof BaseAddress> | null>(null);
const documentRef = ref<InstanceType<typeof BaseDocument> | null>(null);
const contractsRef = ref<InstanceType<typeof BaseContracts> | null>(null);
const componentClabeRef = ref<InstanceType<typeof BaseBankMex> | null>(null);
const componentPixRef = ref<InstanceType<typeof BasePix> | null>(null);
// @ts-ignore
const bankAccountsRef = ref<InstanceType<typeof BaseBankAccounts> | null>(null);
const componentInfosRef = ref<InstanceType<typeof BaseInfos> | null>(null);

const currentEditingName = computed<string>(() => {
  if (emailRef.value?.isEditing) return 'email';
  if (phoneRef.value?.isEditing) return 'phone';
  if (addressRef.value?.isEditing) return 'address';
  if (documentRef.value?.isEditing) return 'document';
  if (contractsRef.value?.isEditing) return 'contracts';
  if (componentClabeRef.value?.isEditing) return 'clabe';
  if (componentPixRef.value?.isEditing) return 'pix';
  if (bankAccountsRef.value?.isEditing) return 'bank-accounts';
  if (componentInfosRef.value?.isEditing) return 'infos';

  return '';
});

const isUserProfileNotLoaded = computed<boolean>(() => {
  return !userEmail.value || !userCountry.value || !userHasDocument.value;
});

function checkCurrentOpenEdit(val: string) {
  if (!currentEditingName.value) {
    return;
  }

  return val !== currentEditingName.value;
}

const refsForPendencyList = computed<Ref['value']>(() => {
  const refsList: Ref['value'] = [];

  if (isUserProfileNotLoaded.value) {
    return refsList;
  }

  refsList.push(
    emailRef.value,
    phoneRef.value,
    addressRef.value,
    documentRef.value,
  );

  if (isContractsSectionEnabled.value) {
    refsList.push(contractsRef.value);
  }
  if (userCurrency.value === 'MXN') {
    refsList.push(componentClabeRef.value);
  }
  if (userCurrency.value === 'BRL') {
    refsList.push(componentPixRef.value);
  }

  if (isBankAccountEnabled.value) {
    refsList.push(bankAccountsRef.value);
  } else {
    refsList.push(componentPixRef.value);
  }

  refsList.push(componentInfosRef.value);

  return refsList;
});

const showWithdrawAlertOnTop = computed(() => {
  return (
    walletRealNumber.value * 100 >= getMinValueToTriggerModule('first_withdraw')
  );
});

onBeforeMount(() => {
  // o UserV2ScreenAccountData cuida do próprio fetch quando o flag está
  // ligado — sem o guard, dispara fetchUserProfile/fetchEmailUpdateFeatureFlag
  // duas vezes (page + Screen)
  if (isUserV2Enabled.value) {
    return;
  }

  if (isUserProfileNotLoaded.value) {
    fetchUserProfile({ force: true });
  }
});

onMounted(async () => {
  if (isUserV2Enabled.value) {
    return;
  }

  setCurrentMenuOpenByGroup('user-account');
  isEmailUpdateEnabled.value = await fetchEmailUpdateFeatureFlag();
});

const { goToLocaleRoute } = useRoutes();
const { forceOpenAccordion } = useAccountProtection();

function rowAllowGrow(...names: string[]): boolean {
  return !names.includes(currentEditingName.value);
}

watch(
  () => refsForPendencyList.value,
  (newValues) => {
    if (!route.query.highlight) {
      return;
    }

    if (newValues.every((ref: Ref['value']) => ref !== null)) {
      checkPending(refsForPendencyList.value);
    }
  },
);
</script>

<template>
  <UserV2ScreenAccountData v-if="isUserV2Enabled" />
  <UserAccountPageLayout
    v-else
    on-back="user-account"
    :title="$t('account.account_details')"
  >
    <UserBox>
      <div
        v-if="isWithinMigrationPeriod"
        :class="$style.alertMigration"
      >
        <NuxtIcon
          :class="$style.alertMigrationIcon"
          name="check"
        />
        <span
          v-html="
            $t('account.migration.alert_description', {
              newDomain: userMigrationNewDomain,
            })
          "
        >
        </span>
      </div>

      <UserAccountWithdrawAlert
        v-if="showWithdrawAlertOnTop"
        :on-top="true"
      />

      <div class="flex flex-col gap-6 md:gap-[30px]">
        <div :class="$style.columnsWrapper">
          <section :class="[$style.section, $style.sectionLeft]">
            <h2 :class="$style.sectionTitle">
              {{ $t('account.your_data_section') }}
            </h2>

            <UserAccountUsername
              :allow-grow="rowAllowGrow('document')"
              :opacity="checkCurrentOpenEdit('username')"
            />

            <UserAccountEmail
              ref="emailRef"
              :allow-grow="rowAllowGrow('email', 'contracts')"
              :is-email-update-enabled="isEmailUpdateEnabled"
              :opacity="checkCurrentOpenEdit('email')"
              @check-pendency="checkPending(refsForPendencyList)"
            />

            <UserAccountPhone
              ref="phoneRef"
              :allow-grow="
                rowAllowGrow('phone', 'pix', 'clabe', 'bank-accounts')
              "
              :opacity="checkCurrentOpenEdit('phone')"
              @check-pendency="checkPending(refsForPendencyList)"
            />

            <UserAccountAddress
              ref="addressRef"
              :allow-grow="rowAllowGrow('address', 'infos')"
              :opacity="checkCurrentOpenEdit('address')"
              @check-pendency="checkPending(refsForPendencyList)"
            />
          </section>

          <section :class="[$style.section, $style.sectionRight]">
            <h2 :class="$style.sectionTitle">
              {{ $t('account.documents_section') }}
            </h2>

            <UserAccountDocument
              ref="documentRef"
              :allow-grow="rowAllowGrow('document')"
              :opacity="checkCurrentOpenEdit('document')"
              @check-pendency="checkPending(refsForPendencyList)"
            />

            <UserAccountContracts
              v-if="isContractsSectionEnabled"
              ref="contractsRef"
              :allow-grow="rowAllowGrow('email', 'contracts')"
              :opacity="checkCurrentOpenEdit('contracts')"
              @check-pendency="checkPending(refsForPendencyList)"
            />
            <div
              v-else
              :class="$style.contractsPlaceholder"
            />

            <div :class="$style.bankWrapper">
              <UserAccountBankAccountsMex
                v-if="userCurrency === 'MXN'"
                id="clabe"
                ref="componentClabeRef"
                :allow-grow="
                  rowAllowGrow('phone', 'pix', 'clabe', 'bank-accounts')
                "
                :opacity="checkCurrentOpenEdit('clabe')"
                @check-pendency="checkPending(refsForPendencyList)"
              />
              <UserAccountBankAccounts
                v-else-if="userCurrency === 'BRL' && isBankAccountEnabled"
                ref="bankAccountsRef"
                :allow-grow="
                  rowAllowGrow('phone', 'pix', 'clabe', 'bank-accounts')
                "
                :opacity="checkCurrentOpenEdit('bank-accounts')"
                @check-pendency="checkPending(refsForPendencyList)"
              />
              <UserAccountPix
                v-else-if="userCurrency === 'BRL'"
                ref="componentPixRef"
                :allow-grow="
                  rowAllowGrow('phone', 'pix', 'clabe', 'bank-accounts')
                "
                :opacity="checkCurrentOpenEdit('pix')"
                @check-pendency="checkPending(refsForPendencyList)"
              />
            </div>

            <UserAccountInfos
              ref="componentInfosRef"
              :allow-grow="rowAllowGrow('address', 'infos')"
              :opacity="checkCurrentOpenEdit('infos')"
            />

            <!-- BUG-04 (auditoria): sem gate, aparecia pra MXN (campos
                 agência/conta não fazem sentido pra CLABE) e dobraria com
                 UserAccountBankAccounts se a flag virar true. -->
            <UserAccountBankAccountsPayerAccounts
              v-if="userCurrency === 'BRL' && !isBankAccountEnabled"
            />
          </section>
        </div>

        <UserAccountUpdates :opacity="checkCurrentOpenEdit('updates')" />

        <button
          :class="$style.deleteAccountBtn"
          type="button"
          @click="
            forceOpenAccordion(AccountProtectionAccordion.Exclusion);
            goToLocaleRoute({
              name: 'user-account-protection',
              query: { section: 'exclusion' },
            });
          "
        >
          <svg
            aria-hidden="true"
            fill="none"
            height="12"
            viewBox="0 0 14 12"
            width="14"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2.12021 3C2.12021 2.20435 2.41805 1.44129 2.9482 0.87868C3.47836 0.316071 4.1974 0 4.94715 0C5.6969 0 6.41595 0.316071 6.9461 0.87868C7.47626 1.44129 7.7741 2.20435 7.7741 3C7.7741 3.79565 7.47626 4.55871 6.9461 5.12132C6.41595 5.68393 5.6969 6 4.94715 6C4.1974 6 3.47836 5.68393 2.9482 5.12132C2.41805 4.55871 2.12021 3.79565 2.12021 3ZM8.48083 7.125L9.8943 12H0L1.41347 7.125H8.48083ZM14 3.75L13.6245 4.14844L12.5865 5.25L13.6245 6.35156L14 6.75L13.2513 7.54453L12.8758 7.14609L11.8378 6.04453L10.7998 7.14609L10.4244 7.54453L9.67566 6.75L10.0511 6.35156L11.0891 5.25L10.0511 4.14844L9.67566 3.75L10.4244 2.95547L10.7998 3.35391L11.8378 4.45547L12.8758 3.35391L13.2513 2.95547L14 3.75Z"
              fill="#FF5454"
            />
          </svg>
          {{ $t('account.delete_account') }}
        </button>
      </div>

      <UserAccountWithdrawAlert
        v-if="!showWithdrawAlertOnTop"
        :on-top="false"
      />
    </UserBox>
  </UserAccountPageLayout>
</template>

<style module lang="scss">
.columnsWrapper {
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: repeat(6, auto);
    column-gap: 20px;
    row-gap: 30px;
  }
}

.section {
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (min-width: 768px) {
    display: grid;
    grid-template-rows: subgrid;
    gap: 15px;
  }
}

.sectionLeft {
  @media (min-width: 768px) {
    grid-column: 1;
    grid-row: 1 / span 6;
  }
}

.sectionRight {
  @media (min-width: 768px) {
    grid-column: 2;
    grid-row: 1 / span 6;
  }
}

.sectionTitle {
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  line-height: 100%;
}

.contractsPlaceholder {
  @apply hidden md:block;
}

.bankWrapper {
  display: contents;
}

.deleteAccountBtn {
  display: flex;
  height: 42px;
  padding: 12px 16px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  border-radius: 10px;
  background: rgba(78, 22, 22, 0.5);
  backdrop-filter: blur(10px);
  color: #ff5454;
  font-size: 11px;
  font-weight: 600;
  line-height: 16px;
  text-transform: uppercase;
}

.alertMigration {
  @apply bg-success/10 text-success font-semibold text-sm p-4 flex items-center flex-col md:flex-row gap-3 rounded-lg mb-5 text-center md:text-left;

  .alertMigrationIcon {
    @apply bg-success/30 rounded-full min-w-9 w-9 h-9 flex items-center justify-center text-xl;
  }
}
</style>
